// Pure geometry helpers for the norms validator (Stage 4).
//
// Everything is in local metres (x east, y south) — same frame as store
// objects and plot polygon. Distance results are in metres.
//
// We intentionally avoid Turf.js (60-80 KB) because all we need is:
//   point ↔ segment, segment ↔ segment, rect ↔ rect, rect ↔ polygon,
//   point-in-polygon. Each is ~10 lines of boring JS.

import { rectCorners } from "./shadows"

export { rectCorners }

// Shortest distance from point p to segment [a, b]. All inputs are [x, y] in metres.
export function distancePointToSegmentM(p, a, b) {
  const dx = b[0] - a[0]
  const dy = b[1] - a[1]
  const len2 = dx * dx + dy * dy
  if (len2 === 0) return Math.hypot(p[0] - a[0], p[1] - a[1])
  let t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2
  if (t < 0) t = 0
  else if (t > 1) t = 1
  const qx = a[0] + t * dx
  const qy = a[1] + t * dy
  return Math.hypot(p[0] - qx, p[1] - qy)
}

// Shortest distance between two segments. Handles the intersection case
// (distance 0) via a 2D cross-product orientation test, otherwise returns
// min of the four point-to-segment distances.
export function distanceSegmentToSegmentM(a1, a2, b1, b2) {
  if (segmentsIntersect(a1, a2, b1, b2)) return 0
  return Math.min(
    distancePointToSegmentM(a1, b1, b2),
    distancePointToSegmentM(a2, b1, b2),
    distancePointToSegmentM(b1, a1, a2),
    distancePointToSegmentM(b2, a1, a2),
  )
}

function orient(a, b, c) {
  const v = (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0])
  if (v > 0) return 1
  if (v < 0) return -1
  return 0
}

function onSegment(a, b, c) {
  return Math.min(a[0], b[0]) <= c[0] && c[0] <= Math.max(a[0], b[0]) &&
         Math.min(a[1], b[1]) <= c[1] && c[1] <= Math.max(a[1], b[1])
}

function segmentsIntersect(a1, a2, b1, b2) {
  const o1 = orient(a1, a2, b1)
  const o2 = orient(a1, a2, b2)
  const o3 = orient(b1, b2, a1)
  const o4 = orient(b1, b2, a2)
  if (o1 !== o2 && o3 !== o4) return true
  if (o1 === 0 && onSegment(a1, a2, b1)) return true
  if (o2 === 0 && onSegment(a1, a2, b2)) return true
  if (o3 === 0 && onSegment(b1, b2, a1)) return true
  if (o4 === 0 && onSegment(b1, b2, a2)) return true
  return false
}

// Ray-casting point-in-polygon. polygon is [[x,y], ...]; the last vertex
// may or may not duplicate the first (both shapes work).
// The (yi > p[1]) !== (yj > p[1]) guard filters horizontal edges (yi === yj)
// before the division, so the denominator yj - yi is never zero here.
export function pointInPolygonM(p, polygon) {
  let inside = false
  const n = polygon.length
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i][0], yi = polygon[i][1]
    const xj = polygon[j][0], yj = polygon[j][1]
    const intersects = ((yi > p[1]) !== (yj > p[1])) &&
                       (p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi)
    if (intersects) inside = !inside
  }
  return inside
}

// Edges of a polygon as [ [a,b], ... ] pairs, closing the ring.
export function polygonEdges(polygon) {
  const n = polygon.length
  if (n < 2) return []
  // Drop duplicate closing vertex if present.
  const last = polygon[n - 1]
  const first = polygon[0]
  const closed = last[0] === first[0] && last[1] === first[1]
  const count = closed ? n - 1 : n
  const edges = []
  for (let i = 0; i < count; i++) {
    edges.push([polygon[i], polygon[(i + 1) % count]])
  }
  return edges
}

// Distance between two axis-aligned-or-rotated rectangles (given as objects
// with xM/yM/widthM/heightM/rotationDeg). Returns 0 when they touch or overlap.
export function distanceRectToRectM(a, b) {
  const ca = rectCorners(a)
  const cb = rectCorners(b)
  if (polygonsOverlap(ca, cb)) return 0
  let min = Infinity
  for (let i = 0; i < 4; i++) {
    const a1 = ca[i], a2 = ca[(i + 1) % 4]
    for (let j = 0; j < 4; j++) {
      const b1 = cb[j], b2 = cb[(j + 1) % 4]
      const d = distanceSegmentToSegmentM(a1, a2, b1, b2)
      if (d < min) min = d
      if (min === 0) return 0
    }
  }
  return min
}

// Rectangle ↔ polygon (the plot). When the object is fully inside the
// polygon this returns the distance from the nearest object edge to the
// nearest plot edge (i.e. how much clearance to the boundary). When the
// object pokes across the boundary, returns 0 (the boundary is crossed).
export function distanceRectToPolygonM(rect, polygon) {
  const corners = rectCorners(rect)
  // If any corner is outside the polygon → object is not fully contained
  // and is crossing or outside the boundary.
  for (const c of corners) {
    if (!pointInPolygonM(c, polygon)) return 0
  }
  // Any polygon vertex inside the rect means the boundary crosses through
  // the rect (the rect "swallows" a corner of the plot).
  for (const v of polygon) {
    if (pointInRect(v, rect)) return 0
  }
  // Otherwise: min distance from rect edges to polygon edges.
  const edgesA = [
    [corners[0], corners[1]],
    [corners[1], corners[2]],
    [corners[2], corners[3]],
    [corners[3], corners[0]],
  ]
  const edgesP = polygonEdges(polygon)
  let min = Infinity
  for (const [a1, a2] of edgesA) {
    for (const [p1, p2] of edgesP) {
      const d = distanceSegmentToSegmentM(a1, a2, p1, p2)
      if (d < min) min = d
    }
  }
  return min
}

function pointInRect(p, rect) {
  const corners = rectCorners(rect)
  return pointInPolygonM(p, corners)
}

// Transform a polygon from a house's LOCAL plan frame into the PLOT frame:
// translate so `anchor` (the plan's bounding-box min — i.e. its top-left) lands
// on the object's (xM, yM), then rotate about that origin by the object's
// rotation. This is the single source of truth for the plan↔plot mapping used
// by the footprint, the plan indicator and per-room insolation.
export function localToPlotPolygon(localPolygon, obj, anchor) {
  const rad = ((obj.rotationDeg ?? 0) * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return localPolygon.map(([vx, vy]) => {
    const lx = vx - anchor[0]
    const ly = vy - anchor[1]
    return [obj.xM + lx * cos - ly * sin, obj.yM + lx * sin + ly * cos]
  })
}

export function polygonMin(polygon) {
  let minX = Infinity, minY = Infinity
  for (const [x, y] of polygon) {
    if (x < minX) minX = x
    if (y < minY) minY = y
  }
  return [minX, minY]
}

// The plot-frame footprint polygon of an object (Stage 9b). A house with a
// floor plan reports its real outer contour (transformed into the plot frame,
// anchored at the object's top-left and rotated like the rect — identical to
// rectCorners' convention and editor_controller's plan indicator); every other
// object (and a house with no plan) reports its rectangle corners. So the norms
// validator sees a concave L-house as an L, not as its bounding box.
export function objectFootprintPolygon(obj) {
  const floor = obj?.houseDesign?.floors?.[0]
  const fp = floor?.footprint
  if (!Array.isArray(fp) || fp.length < 3) return rectCorners(obj)
  // planOrigin (graph bbox min) is the anchor the editor maps to (obj.xM, obj.yM);
  // fall back to the footprint's own min for designs saved before planOrigin
  // existed (single-component plans, where the two coincide).
  const anchor = Array.isArray(floor.planOrigin) ? floor.planOrigin : polygonMin(fp)
  return localToPlotPolygon(fp, obj, anchor)
}

// Min distance between two simple polygons; 0 when they overlap or one contains
// the other. Generalises distanceRectToRectM (rect corners are just a 4-gon).
export function distancePolygonsM(a, b) {
  if (polygonsOverlap(a, b)) return 0
  let min = Infinity
  const ea = polygonEdges(a)
  const eb = polygonEdges(b)
  for (const [a1, a2] of ea) {
    for (const [b1, b2] of eb) {
      const d = distanceSegmentToSegmentM(a1, a2, b1, b2)
      if (d < min) min = d
    }
  }
  return min
}

// Clearance of a polygon that should sit INSIDE `outer` (the plot) to that
// boundary. Returns 0 when `inner` crosses or pokes outside the boundary (any
// inner vertex outside, or any outer vertex swallowed by inner), else the min
// distance from inner edges to outer edges. Generalises distanceRectToPolygonM.
export function distancePolygonInsideToBoundaryM(inner, outer) {
  for (const c of inner) {
    if (!pointInPolygonM(c, outer)) return 0
  }
  for (const v of outer) {
    if (pointInPolygonM(v, inner)) return 0
  }
  let min = Infinity
  const ei = polygonEdges(inner)
  const eo = polygonEdges(outer)
  for (const [a1, a2] of ei) {
    for (const [p1, p2] of eo) {
      const d = distanceSegmentToSegmentM(a1, a2, p1, p2)
      if (d < min) min = d
    }
  }
  return min
}

// Overlap test for two simple polygons: AABB reject → any-vertex-inside
// check (ray-cast) → any-edge-intersection check. Correct for convex and
// concave polygons, returns true when interiors share any point or edges
// touch. For the rect↔rect caller this is 4+4 vertices + 16 edge pairs —
// fast enough to skip a proper SAT implementation.
function polygonsOverlap(a, b) {
  // Fast reject via axis-aligned bounding boxes.
  const bbA = polygonBB(a)
  const bbB = polygonBB(b)
  if (bbA.maxX < bbB.minX || bbB.maxX < bbA.minX ||
      bbA.maxY < bbB.minY || bbB.maxY < bbA.minY) return false
  // Any vertex of A inside B → overlap.
  for (const p of a) if (pointInPolygonM(p, b)) return true
  for (const p of b) if (pointInPolygonM(p, a)) return true
  // Edge intersection → overlap.
  for (let i = 0; i < a.length; i++) {
    const a1 = a[i], a2 = a[(i + 1) % a.length]
    for (let j = 0; j < b.length; j++) {
      const b1 = b[j], b2 = b[(j + 1) % b.length]
      if (segmentsIntersect(a1, a2, b1, b2)) return true
    }
  }
  return false
}

function polygonBB(poly) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const [x, y] of poly) {
    if (x < minX) minX = x
    if (y < minY) minY = y
    if (x > maxX) maxX = x
    if (y > maxY) maxY = y
  }
  return { minX, minY, maxX, maxY }
}
